/**
 * SENTLO AUTHENTICATION MODAL
 * 
 * This component handles user authentication (sign up & sign in) with automatic error recovery.
 * 
 * COMMON ISSUES & AUTO-FIXES:
 * 
 * 1. User created in Supabase Dashboard without password:
 *    - SYMPTOM: "Invalid login credentials" error
 *    - AUTO-FIX: System detects missing password and sets it automatically
 *    - MANUAL FIX: Use Debug Information panel → "Set Password Now" button
 * 
 * 2. Email not confirmed:
 *    - SYMPTOM: "Email not confirmed" error  
 *    - AUTO-FIX: System auto-verifies email on login attempt
 *    - MANUAL FIX: Use Debug Information panel → "Verify Email Now" button
 * 
 * 3. User doesn't exist:
 *    - SYMPTOM: "Invalid login credentials" error
 *    - FIX: User must sign up first
 * 
 * DEBUGGING:
 * - Check browser console for detailed logs
 * - Look for "Debug Information" panel at bottom of form (appears after failed login)
 * - Backend endpoints: /dev/check-user-status, /dev/verify-email, /dev/set-password
 * 
 * BACKEND INTEGRATION:
 * - Sign Up: POST /make-server-684c7e75/auth/signup (creates user with password + auto-confirms email)
 * - Sign In: Direct Supabase client call (supabase.auth.signInWithPassword)
 * - Password Reset: POST /make-server-684c7e75/auth/request-reset
 */

import React from 'react';
import { X, Mail, Lock, User, ArrowRight, ShieldCheck, Eye, EyeOff, Smartphone, Fingerprint, UserCircle, ShieldAlert, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { detectCountry, CountryCode } from '../utils/country-utils';
import { getUserLocation, formatLocation, getLocationEmoji, LocationInfo } from '../utils/location-utils';
import { Logo } from './Logo';
import { authService } from '../services/api';
import * as config from '../../../utils/supabase/info.tsx';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: { name: string; email: string; isVerified?: boolean; country: CountryCode; state: string }, isNewUser: boolean) => void;
}

export function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const [isRegister, setIsRegister] = React.useState(false); // Default to login
  const [authStep, setAuthStep] = React.useState<'identifier' | 'verification' | 'new_password'>('identifier');
  const [isForgotPassword, setIsForgotPassword] = React.useState(false);
  const [isBiometricPrompt, setIsBiometricPrompt] = React.useState(false);
  const [identifier, setIdentifier] = React.useState('');
  const [identifierType, setIdentifierType] = React.useState<'email' | 'phone' | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [otpSent, setOtpSent] = React.useState(false);
  const [otp, setOtp] = React.useState(['', '', '', '', '', '']);
  const [timer, setTimer] = React.useState(0);
  const [formData, setFormData] = React.useState({ 
    firstName: '', 
    surname: '', 
    password: ''
  });
  const [showDebugInfo, setShowDebugInfo] = React.useState(false);
  const [userDebugInfo, setUserDebugInfo] = React.useState<any>(null);

  // Mock persistence for demo purposes
  const getRegisteredUsers = () => {
    const users = localStorage.getItem('sentlo_users');
    return users ? JSON.parse(users) : [];
  };

  const getBlacklist = () => {
    const blacklist = localStorage.getItem('sentlo_blacklist');
    return blacklist ? JSON.parse(blacklist) : [];
  };

  const saveUser = (user: any) => {
    const users = getRegisteredUsers();
    const existingIndex = users.findIndex((u: any) => u.email === user.email);
    if (existingIndex > -1) {
      users[existingIndex] = { ...users[existingIndex], ...user, status: users[existingIndex].status || 'active' };
      localStorage.setItem('sentlo_users', JSON.stringify(users));
    } else {
      localStorage.setItem('sentlo_users', JSON.stringify([...users, { ...user, status: 'active' }]));
    }
  };

  const findUser = (id: string) => {
    return getRegisteredUsers().find((u: any) => u.email === id);
  };

  const isBlacklisted = (id: string) => {
    return getBlacklist().includes(id);
  };

  const getBiometricUser = () => {
    const bioUser = localStorage.getItem('sentlo_bio_user');
    return bioUser ? findUser(bioUser) : null;
  };

  const registerBiometricCredential = async (email: string) => {
    try {
      // Check if Web Authentication API is available
      if (!window.PublicKeyCredential) {
        console.log('⚠️ WebAuthn not supported, skipping biometric registration');
        return;
      }

      // Check if we're in a secure context (HTTPS or localhost)
      if (!window.isSecureContext) {
        console.log('⚠️ Biometrics require secure context (HTTPS), skipping');
        return;
      }

      // Check if credential already exists
      const existingCredential = localStorage.getItem(`sentlo_bio_credential_${email}`);
      if (existingCredential) {
        console.log('✅ Biometric credential already registered');
        return;
      }

      // Create registration challenge
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);
      
      // Create user ID
      const userId = new Uint8Array(16);
      crypto.getRandomValues(userId);

      // Public key credential creation options
      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge: challenge,
        rp: {
          name: "Sentlo",
          id: window.location.hostname
        },
        user: {
          id: userId,
          name: email,
          displayName: email
        },
        pubKeyCredParams: [
          { alg: -7, type: "public-key" },  // ES256
          { alg: -257, type: "public-key" } // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required",
          requireResidentKey: false
        },
        timeout: 60000,
        attestation: "none"
      };

      console.log('🔐 Registering biometric credential...');
      
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      }) as PublicKeyCredential;

      if (credential && credential.rawId) {
        // Store credential ID in localStorage
        const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
        localStorage.setItem(`sentlo_bio_credential_${email}`, credentialId);
        console.log('✅ Biometric credential registered successfully');
        
        toast.success('Biometrics Enabled', {
          description: 'You can now use fingerprint or face ID to sign in next time.',
          duration: 5000
        });
      }
    } catch (error: any) {
      // Biometric registration is completely optional - silently fail
      // Only log as info, not as error, since this is expected behavior
      if (error.name === 'NotAllowedError') {
        console.log('ℹ️ Biometric registration declined or blocked by browser permissions policy');
      } else {
        console.log('ℹ️ Biometric registration not available:', error.name);
      }
      // Don't show any error to user - biometrics are optional
    }
  };

  const handleBiometricLogin = async () => {
    setIsBiometricPrompt(true);
    
    try {
      // Check if Web Authentication API is available
      if (!window.PublicKeyCredential) {
        setIsBiometricPrompt(false);
        toast.error('Biometrics Not Supported', { 
          description: 'Your browser does not support biometric authentication.' 
        });
        return;
      }

      const user = getBiometricUser();
      if (!user) {
        setIsBiometricPrompt(false);
        toast.error('Biometrics Not Enabled', { 
          description: 'Please sign in with your password first to enable biometrics.' 
        });
        return;
      }

      // Get stored credential ID for this user
      const storedCredentialId = localStorage.getItem(`sentlo_bio_credential_${user.email}`);
      
      if (!storedCredentialId) {
        setIsBiometricPrompt(false);
        toast.error('Biometrics Not Set Up', { 
          description: 'Please sign in with password first to set up biometric authentication.' 
        });
        return;
      }

      // Create authentication challenge
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);
      
      // Convert stored credential ID from base64
      const credentialIdBuffer = Uint8Array.from(atob(storedCredentialId), c => c.charCodeAt(0));

      // Request biometric authentication
      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge: challenge,
        allowCredentials: [{
          id: credentialIdBuffer,
          type: 'public-key',
          transports: ['internal'] as AuthenticatorTransport[]
        }],
        timeout: 60000,
        userVerification: 'required'
      };

      console.log('🔐 Requesting biometric authentication...');
      
      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions
      }) as PublicKeyCredential;

      if (assertion) {
        console.log('✅ Biometric authentication successful!');
        setIsBiometricPrompt(false);
        
        // Log successful biometric login
        const loginHistory = JSON.parse(localStorage.getItem('sentlo_login_history') || '[]');
        loginHistory.unshift({
          timestamp: new Date().toISOString(),
          method: 'biometric',
          email: user.email
        });
        if (loginHistory.length > 10) loginHistory.pop();
        localStorage.setItem('sentlo_login_history', JSON.stringify(loginHistory));
        
        toast.success('Identity Verified', { 
          description: `Welcome back, ${user.name}! Authenticated via biometrics.`,
          duration: 4000
        });
        
        onSuccess(user, false);
        onClose();
      } else {
        throw new Error('Authentication failed');
      }
      
    } catch (error: any) {
      console.error('❌ Biometric authentication failed:', error);
      setIsBiometricPrompt(false);
      
      if (error.name === 'NotAllowedError') {
        toast.error('Authentication Cancelled', { 
          description: 'Biometric authentication was cancelled or failed.' 
        });
      } else if (error.name === 'InvalidStateError') {
        toast.error('Biometrics Not Available', { 
          description: 'Please use password to sign in.' 
        });
      } else {
        toast.error('Biometrics Failed', { 
          description: 'Authentication failed. Please sign in with your password.' 
        });
      }
    }
  };

  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return 0;
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const strengthLabels = ['Weak', 'Fair', 'Strong', 'Best Security'];
  const strengthColors = ['bg-red-500', 'bg-amber-500', 'bg-blue-500', 'bg-green-600'];

  const otpInputs = React.useRef<(HTMLInputElement | null)[]>([]);

  // Log helpful information when modal opens
  React.useEffect(() => {
    if (isOpen) {
      console.log('');
      console.log('═══════════════════════════════════════════');
      console.log('🔐 SENTLO AUTHENTICATION MODAL OPENED');
      console.log('═══════════════════════════════════════════');
      console.log('');
      console.log('Common Issues & Solutions:');
      console.log('');
      console.log('1️⃣  "Invalid login credentials" error:');
      console.log('   → User may have been created in Supabase dashboard without password');
      console.log('   → Use the Debug Information panel below the form');
      console.log('   → Click "Set Password Now" to fix automatically');
      console.log('');
      console.log('2️⃣  "Email not confirmed" error:');
      console.log('   → System will auto-verify on login attempt');
      console.log('   → Or use Debug Information panel to verify manually');
      console.log('');
      console.log('3️⃣  User created through signup (/auth/signup):');
      console.log('   → Email auto-confirmed ✓');
      console.log('   → Password automatically set ✓');
      console.log('   → Should work immediately ✓');
      console.log('');
      console.log('═══════════════════════════════════════════');
      console.log('');
    }
  }, [isOpen]);

  const detectIdentifierType = (val: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Only allow email, no phone number
    if (emailRegex.test(val)) return 'email';
    return null;
  };

  const handleSendOtp = async (method: 'sms' | 'whatsapp' | 'call' | 'voice' | 'email' = 'email') => {
    setOtpSent(true);
    setTimer(60);
    const methodLabels = {
      whatsapp: 'WhatsApp',
      sms: 'SMS',
      call: 'Flash Call',
      voice: 'Voice OTP',
      email: 'Email'
    };
    const methodLabel = methodLabels[method];
    
    // For forgot password, send real OTP via backend
    if (isForgotPassword && method === 'email') {
      try {
        const response = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/auth/request-reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${config.publicAnonKey}`
          },
          body: JSON.stringify({ identifier })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          toast.error('Failed to Send Code', { 
            description: data.error || 'Unable to send verification code'
          });
          setOtpSent(false);
          setTimer(0);
          return;
        }
        
        // Check if in development mode (Resend testing restriction)
        if (data.devMode && data.otp) {
          toast.success('Development Mode', { 
            description: `Your verification code is: ${data.otp}`,
            duration: 10000, // Show for 10 seconds
            icon: <span className="text-lg">🔐</span>
          });
          
          console.log('🔐 DEV MODE - Password reset OTP:', data.otp);
          console.log('⚠️  Email delivery restricted to verified address only');
          console.log('📋 Copy this code:', data.otp);
          
          // Auto-fill OTP in development mode for convenience
          const otpDigits = data.otp.split('');
          setOtp(otpDigits);
          
          return;
        }
        
        toast.success('Verification Code Sent', { 
          description: data.message,
          icon: <span className="text-lg">📧</span>
        });
        
        console.log('✅ Password reset OTP requested successfully');
        return;
        
      } catch (error) {
        console.error('❌ Error requesting password reset OTP:', error);
        toast.error('Network Error', { 
          description: 'Unable to connect to server. Please check your connection.'
        });
        setOtpSent(false);
        setTimer(0);
        return;
      }
    }
    
    // For other methods, show mock UI (can be implemented later)
    let description = `Verification code sent to ${identifier} via ${methodLabel}`;
    if (method === 'call') {
      description = 'You will receive a call shortly. The last 6 digits of the calling number is your code.';
    } else if (method === 'voice') {
      description = 'You will receive a call. Please listen carefully as a voice reads your 6-digit code.';
    } else if (method === 'email') {
      description = `Check your email inbox for the verification code sent to ${identifier}`;
    }

    toast.success('Verification Code Sent', { 
      description,
      icon: method === 'email' ? <span className="text-lg">📧</span> :
            (method === 'whatsapp' ? <span className="text-lg">💬</span> : 
            (method === 'call' ? <span className="text-lg">📞</span> : 
            (method === 'voice' ? <span className="text-lg">🗣️</span> : undefined)))
    });

    if (method === 'whatsapp') {
      const cleanPhone = identifier.replace(/\D/g, '');
      console.log('WhatsApp verification triggered for', identifier);
    } else if (method === 'email') {
      console.log('Email verification triggered for', identifier);
    }
  };

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    const type = detectIdentifierType(identifier);
    if (!type) {
      toast.error('Invalid Email', { description: 'Please enter a valid email address.' });
      return;
    }

    if (isBlacklisted(identifier)) {
      toast.error('Access Denied', { 
        description: 'This account has been permanently restricted from the Sentlo network.',
        icon: <ShieldAlert className="w-5 h-5 text-red-600" />
      });
      return;
    }

    const existingUser = findUser(identifier);
    if (existingUser && (existingUser.status === 'blocked' || existingUser.status === 'suspended')) {
      toast.error(`Account ${existingUser.status.charAt(0).toUpperCase() + existingUser.status.slice(1)}`, { 
        description: `Your account access has been ${existingUser.status} due to community policy violations.`,
        icon: <ShieldAlert className="w-5 h-5 text-red-600" />
      });
      return;
    }

    // For registration: Check localStorage for duplicate accounts
    // For login: Skip localStorage check - let Supabase Auth handle it
    if (isRegister && existingUser) {
      toast.error('Account already exists', { 
        description: 'This email or phone is already registered. Please sign in instead.' 
      });
      return;
    }

    // REMOVED: Don't check localStorage for login - Supabase users may not be in localStorage
    // Let Supabase authentication handle user existence validation
    // if (!isRegister && !existingUser) {
    //   toast.error('Account not found');
    //   return;
    // }

    setIdentifierType(type);
    
    // For email: Go directly to password (no OTP verification)
    // For phone: Could add SMS OTP in the future if needed
    if (isRegister || isForgotPassword) {
      // For forgot password with email, send OTP first
      if (isForgotPassword && type === 'email') {
        handleSendOtp('email'); // Send OTP for password reset
        setAuthStep('verification');
      } else if (type === 'email') {
        // For registration with email, skip OTP and go directly to password
        setAuthStep('verification'); // This will show password field for email
        // No OTP sending needed for email - direct password entry
      } else {
        // For phone, you could implement SMS OTP here
        handleSendOtp('whatsapp'); // Show OTP UI for phone
        setAuthStep('verification');
      }
    } else {
      // Direct login flow (enter password)
      setAuthStep('verification');
    }
  };

  const handleCompleteAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🎯 ===== HANDLE COMPLETE AUTH CALLED =====');
    console.log('🎯 authStep:', authStep);
    console.log('🎯 identifierType:', identifierType);
    console.log('🎯 isRegister:', isRegister);
    console.log('🎯 isForgotPassword:', isForgotPassword);
    console.log('🎯 identifier:', identifier);
    console.log('🎯 formData.password exists:', !!formData.password);
    console.log('🎯 formData.password length:', formData.password?.length || 0);
    
    // IF WE ARE IN NEW PASSWORD STEP (Forgot Password path)
    if (authStep === 'new_password') {
      if (passwordStrength < 3) {
        toast.error('Password too weak', { description: 'Please use a stronger password for security.' });
        return;
      }
      
      const user = findUser(identifier);
      if (user) {
        saveUser({ ...user, password: formData.password });
        toast.success('Password Reset Successful', { description: 'You can now sign in with your new password.' });
        setAuthStep('identifier');
        setIsForgotPassword(false);
        setFormData({ ...formData, password: '' });
      }
      return;
    }

    if (identifierType === 'phone' || (isForgotPassword && authStep === 'verification')) {
      if (otp.join('').length !== 6) {
        toast.error('Enter 6-digit code');
        return;
      }
      
      // If this is forgot password flow with email, verify OTP and set new password
      if (isForgotPassword && identifierType === 'email') {
        try {
          const response = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/auth/verify-reset-otp`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${config.publicAnonKey}`
            },
            body: JSON.stringify({ 
              identifier, 
              otp: otp.join(''),
              newPassword: formData.password
            })
          });
          
          const data = await response.json();
          
          if (!response.ok) {
            console.error('❌ Password reset failed!');
            console.error('❌ Response status:', response.status);
            console.error('❌ Error data:', data);
            
            toast.error('Password Reset Failed', { 
              description: data.details || data.error || 'Invalid verification code',
              duration: 8000
            });
            return;
          }
          
          console.log('✅ Password reset successful! Response:', data);
          
          toast.success('Password Reset Complete!', { 
            description: 'Your password has been updated and email verified. Enter your new password below to sign in.',
            duration: 6000
          });
          
          // Keep the identifier but clear OTP and switch to login mode
          // User can now immediately login with their new password
          setAuthStep('identifier');
          setIsForgotPassword(false);
          setOtp(['', '', '', '', '', '']);
          // Keep identifier filled, clear password so user enters it fresh for login
          setFormData({ firstName: '', surname: '', password: '', phone: '' });
          // Keep identifier filled so user doesn't have to re-enter email
          // setIdentifier('') // DON'T clear - keep for easy login
          
          console.log('💡 Ready to login with email:', identifier);
          console.log('💡 Your email has been verified. Enter your new password to sign in.');
          return;
          
        } catch (error) {
          console.error('❌ Error verifying reset OTP:', error);
          toast.error('Network Error', { 
            description: 'Unable to connect to server. Please check your connection.'
          });
          return;
        }
      }
      
      // For other flows (phone signup/login)
      if (isForgotPassword) {
        setAuthStep('new_password');
        return;
      }

      // Get real detected location if available
      const detectedLocation = localStorage.getItem('sentlo_detected_location');
      const locationData = detectedLocation ? JSON.parse(detectedLocation) : null;
      
      const detectedCountry = locationData?.country || detectCountry(identifier);
      const detectedState = locationData?.state || locationData?.city || 
                           (detectedCountry === 'Nigeria' ? 'Lagos' : 
                           (detectedCountry === 'USA' ? 'Texas' : 
                           (detectedCountry === 'UK' ? 'London' : 'Accra')));
      
      const userObj = { 
        name: isRegister ? `New User` : findUser(identifier)?.name || `User ${identifier.slice(-4)}`, 
        email: identifier, 
        isVerified: true,
        country: detectedCountry,
        state: detectedState
      };

      if (isRegister) {
        saveUser(userObj);
      }

      onSuccess(userObj, isRegister);
      onClose();
    } else {
      if (!formData.password && isRegister) {
        toast.error('Please enter a password');
        return;
      }

      // REMOVED: Local password check - let Supabase Auth handle all authentication
      // const existingUser = findUser(identifier);
      // if (!isRegister && existingUser && existingUser.password && existingUser.password !== formData.password) {
      //   toast.error('Incorrect password');
      //   return;
      // }

      // Get real detected location if available
      const detectedLocation = localStorage.getItem('sentlo_detected_location');
      const locationData = detectedLocation ? JSON.parse(detectedLocation) : null;
      
      const detectedCountry = locationData?.country || detectCountry(identifier);
      const detectedState = locationData?.state || locationData?.city || 
                           (detectedCountry === 'Nigeria' ? 'Lagos' : 
                           (detectedCountry === 'USA' ? 'Texas' : 
                           (detectedCountry === 'UK' ? 'London' : 'Accra')));
      
      const existingUser = findUser(identifier);
      const userObj = { 
        name: isRegister ? identifier.split('@')[0] || 'User' : existingUser?.name || identifier.split('@')[0] || 'User', 
        email: identifier,
        password: formData.password,
        country: detectedCountry,
        state: detectedState
      };

      // Try real Supabase authentication for email/password auth
      try {
        if (isRegister) {
          console.log('Attempting real Supabase signup...');
          // Ensure name is at least 2 characters for validation
          const signupName = userObj.name.length >= 2 ? userObj.name : `User_${userObj.name}`;
          console.log('📝 Signup data:', { email: identifier, name: signupName, country: detectedCountry });
          const result = await authService.signUp(identifier, formData.password, signupName, '', detectedCountry, userObj.state);
          console.log('✅ User ID:', result?.user?.id);
          console.log('✅ User email:', result?.user?.email);
          saveUser(userObj); // Also save locally for compatibility
          toast.success('🎉 Account Created Successfully!', { 
            description: `Welcome ${userObj.name}! You can start using Sentlo immediately. No email verification required.`,
            duration: 5000
          });
        } else {
          console.log('🔐 ===== LOGIN ATTEMPT =====');
          console.log('📧 Email:', identifier);
          console.log('🔐 Password provided:', formData.password ? 'YES' : 'NO');
          console.log('🔐 Password length:', formData.password?.length || 0);
          console.log('🔐 Password preview:', formData.password ? formData.password.substring(0, 3) + '...' : 'NO PASSWORD');
          console.log('⏰ Timestamp:', new Date().toISOString());
          
          // Check if password is actually provided
          if (!formData.password || formData.password.trim().length === 0) {
            console.error('❌ No password provided!');
            toast.error('Please enter your password');
            return;
          }
          
          // Check if account is locked due to too many failed attempts
          try {
            const lockCheckResponse = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/auth/check-lockout`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${config.publicAnonKey}`
              },
              body: JSON.stringify({ email: identifier })
            });
            
            if (lockCheckResponse.ok) {
              const lockData = await lockCheckResponse.json();
              
              if (lockData.locked) {
                const minutes = Math.ceil(lockData.remainingSeconds / 60);
                toast.error('Account Temporarily Locked', {
                  description: `Too many failed login attempts. Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`,
                  duration: 8000
                });
                console.log('🔒 Account locked until:', lockData.lockedUntil);
                return;
              }
              
              if (lockData.failedAttempts > 0) {
                const remaining = lockData.maxAttempts - lockData.failedAttempts;
                console.log(`⚠️ Failed attempts: ${lockData.failedAttempts}/${lockData.maxAttempts}. ${remaining} attempts remaining.`);
              }
            }
          } catch (error) {
            console.error('Failed to check account lockout:', error);
            // Continue with login attempt even if lockout check fails
          }
          
          // Detect user location for security logging
          console.log('🌍 Detecting login location...');
          const locationPromise = getUserLocation(false); // IP-based only, no GPS prompt
          
          let loginSuccess = false;
          let result;
          
          try {
            result = await authService.signIn(identifier, formData.password);
            loginSuccess = true;
            console.log('✅ Supabase signin result:', result);
          } catch (loginError: any) {
            loginSuccess = false;
            console.error('❌ Login failed:', loginError);
            // Re-throw to be caught by outer try-catch
            throw loginError;
          } finally {
            // Record the login attempt (success or failure) for brute force protection
            try {
              await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/auth/record-login`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${config.publicAnonKey}`
                },
                body: JSON.stringify({ 
                  email: identifier, 
                  success: loginSuccess 
                })
              });
            } catch (error) {
              console.error('Failed to record login attempt:', error);
            }
          }
          
          // Get location info first, then update user object with real location
          try {
            const location = await locationPromise;
            const locationDisplay = formatLocation(location);
            const locationFlag = getLocationEmoji(location.countryCode);
            
            console.log('📍 Login location detected:', locationDisplay);
            console.log('📍 IP Address:', location.ip || 'Unknown');
            console.log('📍 City:', location.city);
            console.log('📍 State:', location.state);
            console.log('📍 Country:', location.country);
            
            // Update userObj with real detected location
            const updatedUserObj = {
              ...userObj,
              country: location.country || userObj.country,
              state: location.state || location.city || userObj.state
            };
            
            saveUser(updatedUserObj); // Save with real location
            localStorage.setItem('sentlo_bio_user', identifier); // Enable biometrics for next time
            
            // Register biometric credential for future logins (non-blocking)
            registerBiometricCredential(identifier).catch(err => 
              console.log('Biometric registration skipped:', err)
            );
            
            // Store login location history for security
            const loginHistory = JSON.parse(localStorage.getItem('sentlo_login_history') || '[]');
            loginHistory.unshift({
              timestamp: new Date().toISOString(),
              location: locationDisplay,
              ip: location.ip,
              city: location.city,
              state: location.state,
              country: location.country
            });
            // Keep only last 10 logins
            if (loginHistory.length > 10) loginHistory.pop();
            localStorage.setItem('sentlo_login_history', JSON.stringify(loginHistory));
            
            toast.success('Welcome Back!', { 
              description: (
                <div className="space-y-1">
                  <div className="font-bold">{updatedUserObj.name}</div>
                  <div className="flex items-center gap-1.5 text-xs text-zinc-600">
                    <MapPin className="w-3 h-3" />
                    <span>{locationFlag} {locationDisplay}</span>
                  </div>
                </div>
              ),
              duration: 5000
            });
            
            // Pass the updated user object with real location
            onSuccess(updatedUserObj, false);
            onClose();
            return; // Exit early to avoid duplicate onSuccess calls
          } catch (locationError) {
            console.error('❌ Failed to get location:', locationError);
            // Fallback: save and proceed without location detection
            saveUser(userObj);
            localStorage.setItem('sentlo_bio_user', identifier);
            
            // Register biometric credential for future logins (non-blocking)
            registerBiometricCredential(identifier).catch(err => 
              console.log('Biometric registration skipped:', err)
            );
            
            toast.success('Welcome Back!', { 
              description: `${userObj.name}`,
              duration: 4000
            });
            onSuccess(userObj, false);
            onClose();
            return; // Exit early to avoid duplicate onSuccess calls
          }
        }
        
        // This code should only run for registration, not login
        if (isRegister) {
          onSuccess(userObj, isRegister);
          onClose();
        }
      } catch (error: any) {
        console.error('❌ ===== AUTHENTICATION ERROR =====');
        console.error('❌ Error Type:', error?.constructor?.name || 'Unknown');
        console.error('❌ Error Message:', error?.message || 'No message');
        console.error('❌ Error Code:', error?.code || 'No code');
        console.error('❌ Error Status:', error?.status || 'No status');
        console.error('❌ Full Error Object:', JSON.stringify(error, null, 2));
        console.error('❌ Error Stack:', error?.stack || 'No stack');
        console.error('📧 Attempted Email:', identifier);
        console.error('🔐 Password Length:', formData.password?.length || 0);
        console.error('⏰ Error Time:', new Date().toISOString());
        console.error('🚨🚨🚨 DIAGNOSTIC CODE IS LOADING - VERSION 2 🚨🚨🚨');
        console.error('🔍 ===== POST-ERROR PROCESSING STARTED =====');
        
        // Check for specific error messages
        const errorMsg = error?.message?.toLowerCase() || '';
        
        console.error('🔍 ===== ERROR ANALYSIS =====');
        console.error('🔍 Error message:', errorMsg);
        console.error('🔍 Is Register?', isRegister);
        console.error('🔍 Error has code?', !!error?.code);
        console.error('🔍 Error code value:', error?.code);
        
        // SPECIAL CASE: Auto-create admin@sentlo.ng on first login attempt
        if (!isRegister && identifier === 'admin@sentlo.ng' && error?.code === 'invalid_credentials') {
          console.log('🔧 ===== ADMIN ACCOUNT AUTO-CREATION =====');
          console.log('🔧 Detected first-time admin login attempt');
          console.log('🔧 Creating admin account automatically...');
          
          toast.info('Creating Admin Account...', {
            description: 'First time setup detected. Creating your admin account now.',
            duration: 4000
          });
          
          try {
            // Auto-create the admin account
            const createResponse = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/auth/signup`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${config.publicAnonKey}`
              },
              body: JSON.stringify({
                email: identifier,
                password: formData.password,
                name: 'Admin User',
                phone: '08099887766',
                country: 'Nigeria',
                state: 'Lagos',
                lga: ''
              })
            });
            
            const createData = await createResponse.json();
            
            if (createResponse.ok && createData.success) {
              console.log('✅ Admin account created successfully!');
              console.log('✅ User ID:', createData.user.id);
              
              // Now promote to admin
              console.log('🔧 Promoting to admin role...');
              
              const promoteResponse = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/admin/promote-first`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${config.publicAnonKey}`
                },
                body: JSON.stringify({
                  email: identifier,
                  promotionSecret: 'sentlo-super-secret-2026'
                })
              });
              
              const promoteData = await promoteResponse.json();
              
              if (promoteResponse.ok && promoteData.success) {
                console.log('✅ Promoted to admin successfully!');
                
                toast.success('Admin Account Ready!', {
                  description: 'Your admin account has been created. Please click "Sign In" again to login.',
                  duration: 8000,
                  icon: <span className="text-lg">🎉</span>
                });
                
                return;
              } else {
                console.error('❌ Failed to promote to admin:', promoteData.error);
                toast.warning('Account Created', {
                  description: 'Account created but admin promotion failed. You can login as a regular user.',
                  duration: 8000
                });
                return;
              }
            } else {
              console.error('❌ Failed to create account:', createData.error);
              // Continue with normal error handling below
            }
          } catch (autoCreateError) {
            console.error('❌ Auto-creation error:', autoCreateError);
            // Continue with normal error handling below
          }
        }
        
        // For login attempts (not registration), always run diagnostics
        if (!isRegister) {
          console.error('🔍 ===== RUNNING USER DIAGNOSTICS =====');
          console.error('📧 Checking status for:', identifier);
          
          // Try to get more information about why login failed
          try {
            console.log('🔧 Calling diagnostic endpoint...');
            const checkResponse = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/dev/check-user-status`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${config.publicAnonKey}`
              },
              body: JSON.stringify({ email: identifier })
            });
            
            const checkData = await checkResponse.json();
            console.log('👤 User Status Check:', checkData);
            console.log('🔍 DETAILED USER STATUS:', {
              exists: checkData.exists,
              emailConfirmed: checkData.emailConfirmed,
              hasPassword: checkData.hasPassword,
              hasEncryptedPassword: checkData.hasEncryptedPassword,
              hasEverSignedIn: checkData.hasEverSignedIn,
              message: checkData.message
            });
            
            // Store debug info for display
            setUserDebugInfo(checkData);
            
            if (checkData.exists) {
              if (!checkData.emailConfirmed) {
                console.log('📧 Email not confirmed - auto-verifying...');
                
                // Auto-verify the email
                const verifyResponse = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/dev/verify-email`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${config.publicAnonKey}`
                  },
                  body: JSON.stringify({ email: identifier })
                });
                
                const verifyData = await verifyResponse.json();
                
                if (verifyResponse.ok) {
                  toast.success('Email Verified!', {
                    description: 'Your email has been verified. Please try signing in again.',
                    duration: 6000,
                    icon: <span className="text-lg">✅</span>
                  });
                  console.log('✅ Email auto-verified. User should retry login now.');
                  return;
                }
              } else if (checkData.hasPassword === false) {
                console.log('⚠️ User exists but has no password set - setting password now...');
                
                // Auto-set the password they just entered
                const setPasswordResponse = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/dev/set-password`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${config.publicAnonKey}`
                  },
                  body: JSON.stringify({ 
                    email: identifier,
                    password: formData.password
                  })
                });
                
                const setPasswordData = await setPasswordResponse.json();
                
                if (setPasswordResponse.ok) {
                  toast.success('Password Set!', {
                    description: 'Your password has been set. Please sign in again with this password.',
                    duration: 6000,
                    icon: <span className="text-lg">🔐</span>
                  });
                  console.log('✅ Password auto-set. User should retry login now.');
                  return;
                } else {
                  toast.error('Failed to Set Password', {
                    description: setPasswordData.error || 'Please use "Forgot Password" to set a new password.',
                    duration: 8000
                  });
                  return;
                }
              } else if (checkData.hasPassword === true) {
                // User has a password set - this is definitely incorrect password
                toast.error('Incorrect Password', {
                  description: (
                    <div className="space-y-2">
                      <p>The password you entered is incorrect.</p>
                      <p className="text-xs font-semibold text-white">
                        💡 Click "Forgot Password?" below to reset your password
                      </p>
                    </div>
                  ),
                  duration: 8000,
                  icon: <span className="text-lg">🔐</span>
                });
                console.log('🔐 User exists, email confirmed, has password - password is INCORRECT');
                return;
              } else {
                // Fallback - assume incorrect password
                toast.error('Incorrect Password', {
                  description: (
                    <div className="space-y-2">
                      <p>The password you entered is incorrect.</p>
                      <p className="text-xs font-semibold text-white">
                        💡 Click "Forgot Password?" below to reset your password
                      </p>
                    </div>
                  ),
                  duration: 8000,
                  icon: <span className="text-lg">🔐</span>
                });
                console.log('🔐 Password incorrect - advising user to reset password');
                return;
              }
            } else {
              // User doesn't exist - check if this is the admin account attempting first login
              if (identifier === 'admin@sentlo.ng') {
                console.log('🔧 ===== ADMIN ACCOUNT AUTO-CREATION =====');
                console.log('🔧 Detected first-time admin login attempt');
                console.log('🔧 Creating admin account automatically...');
                
                toast.info('Creating Admin Account...', {
                  description: 'First time setup detected. Creating your admin account now.',
                  duration: 4000
                });
                
                try {
                  // Auto-create the admin account
                  const createResponse = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/auth/signup`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${config.publicAnonKey}`
                    },
                    body: JSON.stringify({
                      email: identifier,
                      password: formData.password,
                      name: 'Admin User',
                      phone: '08099887766',
                      country: 'Nigeria',
                      state: 'Lagos',
                      lga: ''
                    })
                  });
                  
                  const createData = await createResponse.json();
                  
                  if (createResponse.ok && createData.success) {
                    console.log('✅ Admin account created successfully!');
                    console.log('✅ User ID:', createData.user.id);
                    
                    // Now promote to admin
                    console.log('🔧 Promoting to admin role...');
                    
                    const promoteResponse = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/admin/promote-first`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${config.publicAnonKey}`
                      },
                      body: JSON.stringify({
                        email: identifier,
                        promotionSecret: 'sentlo-super-secret-2026'
                      })
                    });
                    
                    const promoteData = await promoteResponse.json();
                    
                    if (promoteResponse.ok && promoteData.success) {
                      console.log('✅ Promoted to admin successfully!');
                      
                      toast.success('Admin Account Created!', {
                        description: 'Your admin account has been created and configured. Signing you in now...',
                        duration: 5000,
                        icon: <span className="text-lg">🎉</span>
                      });
                      
                      // Now try to sign in automatically
                      console.log('🔧 Attempting automatic sign in...');
                      
                      // Wait a moment for everything to propagate
                      await new Promise(resolve => setTimeout(resolve, 1000));
                      
                      try {
                        const signInResult = await authService.signIn(identifier, formData.password);
                        console.log('✅ Auto sign-in successful!');
                        
                        // Get location and complete login
                        const location = await getUserLocation(false);
                        const updatedUserObj = {
                          name: 'Admin User',
                          email: identifier,
                          country: location.country || 'Nigeria',
                          state: location.state || location.city || 'Lagos',
                          isVerified: true
                        };
                        
                        saveUser(updatedUserObj);
                        localStorage.setItem('sentlo_bio_user', identifier);
                        
                        toast.success('Welcome Admin!', {
                          description: 'You now have full admin access. Check the Profile tab for the Admin Panel.',
                          duration: 6000
                        });
                        
                        onSuccess(updatedUserObj, false);
                        onClose();
                        return;
                      } catch (signInError) {
                        console.log('⚠️ Auto sign-in failed, user can try manually');
                        toast.success('Account Ready!', {
                          description: 'Your admin account is ready. Please click "Sign In" again to log in.',
                          duration: 6000
                        });
                        return;
                      }
                    } else {
                      console.error('❌ Failed to promote to admin:', promoteData.error);
                      toast.warning('Account Created', {
                        description: 'Account created but admin promotion failed. You can login as a regular user.',
                        duration: 8000
                      });
                      return;
                    }
                  } else {
                    console.error('❌ Failed to create account:', createData.error);
                    toast.error('Auto-Creation Failed', {
                      description: createData.error || 'Could not create account automatically. Please try signing up manually.',
                      duration: 8000
                    });
                    return;
                  }
                } catch (autoCreateError) {
                  console.error('❌ Auto-creation error:', autoCreateError);
                  toast.error('Setup Error', {
                    description: 'Could not auto-create admin account. Please use the signup form.',
                    duration: 8000
                  });
                  return;
                }
              }
              
              // User doesn't exist - check for common typos
              const possibleTypos = [];
              if (identifier.includes('gobal')) {
                possibleTypos.push(identifier.replace('gobal', 'global'));
              }
              if (identifier.includes('@gmai.com')) {
                possibleTypos.push(identifier.replace('@gmai.com', '@gmail.com'));
              }
              if (identifier.includes('@yahooo.com')) {
                possibleTypos.push(identifier.replace('@yahooo.com', '@yahoo.com'));
              }
              
              const typoSuggestion = possibleTypos.length > 0 ? `Did you mean ${possibleTypos[0]}?` : '';
              
              toast.error('Account Not Found', {
                description: (
                  <div className="space-y-1">
                    <div>No account exists with this email.</div>
                    {typoSuggestion && (
                      <div className="text-xs font-medium text-amber-600">
                        {typoSuggestion}
                      </div>
                    )}
                    <div className="text-xs">Please check the spelling or sign up.</div>
                  </div>
                ),
                duration: 8000
              });
              console.log('👤 No user found with this email');
              console.log('💡 Possible typo detected:', typoSuggestion);
              return;
            }
          } catch (checkError) {
            console.error('❌ Failed to check user status:', checkError);
            // Fallback error message
            toast.error('Login Failed', {
              description: 'Could not verify account. Please check your email and password, or try signing up if you don\'t have an account.',
              duration: 6000
            });
          }
        }
        
        // Check if email is not confirmed
        if (error.message && error.message.includes('Email not confirmed')) {
          console.log('💡 Email not confirmed - attempting auto-verification...');
          
          // Auto-verify the email immediately
          try {
            console.log('🔧 Calling verification endpoint for:', identifier);
            
            const response = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/dev/verify-email`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${config.publicAnonKey}`
              },
              body: JSON.stringify({ email: identifier })
            });
            
            const data = await response.json();
            console.log('📡 Verification response:', data);
            
            if (response.ok) {
              toast.success('Email Verified!', {
                description: `${identifier} has been verified. Please sign in again.`,
                duration: 6000,
                icon: <span className="text-lg">✅</span>
              });
              
              console.log('✅ Email auto-verified successfully!');
              console.log('💡 User can now sign in with the same credentials');
            } else {
              toast.error('Verification Failed', {
                description: data.error || 'Unable to verify email. Please contact support.',
                duration: 8000
              });
              console.error('❌ Verification failed:', data.error);
            }
          } catch (err) {
            console.error('❌ Failed to verify email:', err);
            toast.error('Network Error', {
              description: 'Unable to connect to server. Please check your connection.',
              duration: 8000
            });
          }
          
          console.log('📧 Email:', identifier);
          return;
        }
        
        if (false) {
          toast.error('Email Not Verified', {
            description: 'Your email needs to be verified before you can sign in.',
            duration: 10000,
            icon: <span className="text-lg">📧</span>,
            action: {
              label: 'Verify Now',
              onClick: async () => {
                try {
                  const response = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/dev/verify-email`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${config.publicAnonKey}`
                    },
                    body: JSON.stringify({ email: identifier })
                  });
                  
                  const data = await response.json();
                  
                  if (response.ok) {
                    toast.success('Email Verified!', {
                      description: `${identifier} has been verified. Please try signing in again.`,
                      duration: 5000
                    });
                  } else {
                    toast.error('Verification Failed', {
                      description: data.error || 'Unable to verify email'
                    });
                  }
                } catch (err) {
                  console.error('Failed to verify email:', err);
                  toast.error('Network Error', {
                    description: 'Unable to connect to server'
                  });
                }
              }
            }
          });
          
          console.log('💡 TIP: Click "Verify Now" button in the notification to verify instantly');
          console.log('📧 Email:', identifier);
          return;
        }
        
        // Show error to user with helpful guidance
        const errorTitle = isRegister ? 'Signup Failed' : 'Login Failed';
        let errorDescription = error.message || 'Unable to authenticate. Please try again.';
        
        // Make error messages more user-friendly
        if (errorMsg.includes('invalid login credentials') || errorMsg.includes('invalid credentials')) {
          // Check for common typos in the email
          const possibleTypos = [];
          if (identifier.includes('gobal')) {
            possibleTypos.push(identifier.replace('gobal', 'global'));
          }
          if (identifier.includes('@gmai.com')) {
            possibleTypos.push(identifier.replace('@gmai.com', '@gmail.com'));
          }
          
          if (possibleTypos.length > 0) {
            errorDescription = `Account not found. Did you mean "${possibleTypos[0]}"? Check the spelling and try again.`;
          } else {
            errorDescription = 'Incorrect email or password. Please double-check your credentials or sign up if you don\'t have an account.';
          }
        } else if (errorMsg.includes('email not confirmed')) {
          errorDescription = 'Email verification required. We\'ll verify it for you - please try again.';
        } else if (errorMsg.includes('user not found')) {
          errorDescription = 'No account found with this email. Please sign up first.';
        }
        
        toast.error(errorTitle, {
          description: errorDescription,
          duration: 8000
        });
        
        // Log comprehensive debugging information
        console.log('');
        console.log('═══════════════════════════════════════════');
        console.log('🔍 LOGIN TROUBLESHOOTING GUIDE');
        console.log('═══════════════════════════════════════════');
        console.log('');
        console.log('If you created this user in Supabase Dashboard:');
        console.log('1. Users created via dashboard may not have passwords set');
        console.log('2. Their email may not be confirmed');
        console.log('3. Use the "Forgot Password" flow to set a password');
        console.log('');
        console.log('Automatic fixes attempted:');
        console.log('✓ Checked if user exists');
        console.log('✓ Checked email confirmation status');
        console.log('✓ Checked if password is set');
        console.log('');
        console.log('What to do next:');
        console.log('• If error persists, check the console above for user status');
        console.log('• Try using "Forgot Password" to reset');
        console.log('• Or sign up with this email if account doesn\'t exist');
        console.log('═══════════════════════════════════════════');
        console.log('');
        
        // If it's a rate limit error, clear it automatically
        if (error.message && error.message.includes('Too many registration attempts')) {
          toast.info('Rate Limit Reached', {
            description: 'Clearing rate limit for you... Please try again in a moment.',
            duration: 5000
          });
          
          // Clear the rate limit automatically
          setTimeout(async () => {
            try {
              await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/dev/clear-rate-limit`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${config.publicAnonKey}`
                },
                body: JSON.stringify({ ipAddress: 'unknown' })
              });
              toast.success('Rate limit cleared!', {
                description: 'You can now try signing up again.'
              });
            } catch (clearError) {
              console.error('Failed to clear rate limit:', clearError);
            }
          }, 1000);
        }
        
        // If email is already registered, clear the orphaned data
        if (error.message && error.message.includes('Email address is already registered')) {
          toast.info('Cleaning up...', {
            description: 'Clearing old registration data. Please try again.',
            duration: 5000
          });
          
          setTimeout(async () => {
            try {
              await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/dev/clear-user-data`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${config.publicAnonKey}`
                },
                body: JSON.stringify({ email: identifier })
              });
              toast.success('Data cleared!', {
                description: 'You can now try signing up again.'
              });
            } catch (clearError) {
              console.error('Failed to clear user data:', clearError);
            }
          }, 1000);
        }
        
        // Don't fall back to mock auth - let user retry
        return;
      }
    }
  };

  React.useEffect(() => {
    let interval: any;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpInputs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpInputs.current[index - 1]?.focus();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
          />

          {isBiometricPrompt && (
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="absolute z-[60] inset-0 flex items-center justify-center p-6"
            >
              <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center text-center max-w-[280px]">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6 relative">
                  <Fingerprint className="w-10 h-10 text-green-600 animate-pulse" />
                  <div className="absolute inset-0 border-2 border-green-600 rounded-full animate-ping opacity-20" />
                </div>
                <h3 className="text-xl font-black text-zinc-900 mb-2 uppercase tracking-tighter">Sentlo ID</h3>
                <p className="text-sm text-zinc-500">Scanning for FaceID or Fingerprint to secure your access...</p>
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          >
            <div className="p-6 overflow-y-auto">
              <div className="flex flex-col items-center mb-6 pt-4">
                <Logo size="lg" showSlogan={true} className="mb-4" />
                <h2 className="text-2xl font-bold text-zinc-900">
                  {authStep === 'identifier' 
                    ? (isRegister ? 'Join Sentlo' : 'Welcome back') 
                    : authStep === 'new_password' ? 'Reset Password' : 'Verify Identity'}
                </h2>
                <p className="text-sm text-zinc-500 text-center mt-1">
                  {authStep === 'identifier' 
                    ? (isRegister ? 'Create an account to start reporting.' : 'Sign in to your Sentlo account.') 
                    : authStep === 'new_password' ? 'Create a secure new password.' : identifierType === 'phone' || isForgotPassword ? `Enter the code sent to ${identifier}` : `Enter your password for ${identifier}`}
                </p>
              </div>

              {authStep === 'identifier' && (
                <div className="flex bg-zinc-100 p-1 rounded-xl mb-6">
                  <button 
                    onClick={() => { setIsRegister(false); setIsForgotPassword(false); }}
                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${!isRegister ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
                  >
                    Login
                  </button>
                  <button 
                    onClick={() => { setIsRegister(true); setIsForgotPassword(false); }}
                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${isRegister ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
                  >
                    Register
                  </button>
                </div>
              )}
              
              <button 
                onClick={onClose} 
                className="absolute top-4 right-4 p-2 hover:bg-zinc-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-zinc-500" />
              </button>

              {authStep === 'identifier' ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-3">
                    <button 
                      onClick={() => {
                        onSuccess({ name: "Google User", email: "google@user.com" });
                        onClose();
                        toast.success("Signed in with Google");
                      }}
                      className="flex items-center justify-center gap-2 py-3 border border-zinc-200 rounded-xl hover:bg-zinc-50 transition-colors"
                    >
                      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-4 h-4" alt="Google" />
                      <span className="text-xs font-bold text-zinc-700">Google</span>
                    </button>
                    <button 
                      onClick={() => {
                        onSuccess({ name: "Apple User", email: "apple@user.com" });
                        onClose();
                        toast.success("Signed in with Apple");
                      }}
                      className="flex items-center justify-center gap-2 py-3 border border-zinc-200 rounded-xl hover:bg-zinc-50 transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 384 512" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-31.4-57.3-114.8-10.7-142zM263 48c1.5 54.2-46.1 106.1-100.3 105.1-4.7-44 33.5-95.2 81.2-105.1a100.6 100.6 0 0 1 19.1 0z"/></svg>
                      <span className="text-xs font-bold text-zinc-700">Apple</span>
                    </button>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-zinc-100"></div>
                    </div>
                    <div className="relative flex justify-center text-[10px] uppercase font-black tracking-widest">
                      <span className="bg-white px-4 text-zinc-400">Or use phone/email</span>
                    </div>
                  </div>

                  {!isRegister && getBiometricUser() && (
                    <button 
                      onClick={handleBiometricLogin}
                      className="w-full flex items-center justify-center gap-3 py-4 border-2 border-green-600 rounded-xl bg-green-50 text-green-600 font-black uppercase tracking-widest text-xs hover:bg-green-100 transition-all"
                    >
                      <Fingerprint className="w-5 h-5" />
                      Sign in with Biometrics
                    </button>
                  )}

                  <form onSubmit={handleContinue} className="space-y-4">
                    <div className="space-y-1">
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input
                          required
                          type="email"
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-4 pl-10 pr-4 text-sm font-bold focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                          placeholder="Email Address"
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-green-600 text-white py-4 rounded-xl font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-green-700 transition-all group shadow-lg shadow-green-600/20"
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </form>
                </div>
              ) : (
                <form onSubmit={handleCompleteAuth} className="space-y-6">
                  {identifierType === 'phone' || (isForgotPassword && authStep === 'verification') ? (
                    <div className="space-y-4">
                      {/* OTP Input Fields */}
                      <div className="flex flex-col items-center gap-4 mb-2">
                        {identifierType === 'phone' && (
                          <button 
                            type="button"
                            onClick={() => {
                              const cleanPhone = identifier.replace(/\D/g, '');
                              window.open(`https://wa.me/${cleanPhone}`, '_blank');
                            }}
                            className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-2 rounded-full text-xs font-black uppercase tracking-widest hover:bg-[#128C7E] transition-all shadow-md shadow-green-500/20"
                          >
                            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                            Check WhatsApp
                          </button>
                        )}
                        <div className="flex justify-center gap-2">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              ref={(el) => (otpInputs.current[index] = el)}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(index, e.target.value)}
                              onKeyDown={(e) => handleOtpKeyDown(index, e)}
                              className="w-10 h-12 bg-zinc-50 border border-zinc-200 rounded-lg text-center text-lg font-black text-green-600 focus:border-green-600 focus:ring-2 focus:ring-green-600/10 outline-hidden transition-all"
                            />
                          ))}
                        </div>
                      </div>

                      {/* Password field for forgot password flow */}
                      {isForgotPassword && (
                        <div className="space-y-4">
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                            <input
                              required
                              type={showPassword ? 'text' : 'password'}
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-4 pl-10 pr-12 text-sm font-bold focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                              placeholder="Enter New Password"
                              value={formData.password}
                              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-zinc-200/50 rounded-lg transition-colors"
                            >
                              {showPassword ? (
                                <EyeOff className="w-4 h-4 text-zinc-400" />
                              ) : (
                                <Eye className="w-4 h-4 text-zinc-400" />
                              )}
                            </button>
                          </div>

                          {formData.password && (
                            <div className="space-y-2 px-1">
                              <div className="flex justify-between items-center">
                                <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400">Security Strength</span>
                                <span className={`text-[9px] font-black uppercase tracking-widest ${passwordStrength === 4 ? 'text-green-600' : 'text-zinc-500'}`}>
                                  {strengthLabels[passwordStrength - 1]}
                                </span>
                              </div>
                              <div className="flex gap-1 h-1">
                                {[1, 2, 3, 4].map((tier) => (
                                  <div 
                                    key={tier}
                                    className={`flex-1 rounded-full transition-all duration-500 ${
                                      passwordStrength >= tier ? strengthColors[passwordStrength - 1] : 'bg-zinc-100'
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input
                          required
                          type={showPassword ? 'text' : 'password'}
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-4 pl-10 pr-12 text-sm font-bold focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                          placeholder={authStep === 'new_password' ? "Enter New Password" : "Password"}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-zinc-200/50 rounded-lg transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4 text-zinc-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-zinc-400" />
                          )}
                        </button>
                      </div>

                      <div className="flex justify-end px-1">
                        {!isRegister && authStep === 'verification' && (
                          <button 
                            type="button"
                            onClick={() => {
                              setIsForgotPassword(true);
                              setAuthStep('identifier'); // Go back to identifier screen to start reset flow
                              setFormData({ firstName: '', surname: '', password: '', phone: '' });
                              setIdentifier('');
                              toast.info('Password Reset', { description: 'Enter your email or phone to receive a verification code.' });
                            }}
                            className="text-[10px] font-bold text-green-600 hover:underline uppercase tracking-widest"
                          >
                            Forgot Password?
                          </button>
                        )}
                      </div>

                      {formData.password && (
                        <div className="space-y-2 px-1">
                          <div className="flex justify-between items-center">
                            <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400">Security Strength</span>
                            <span className={`text-[9px] font-black uppercase tracking-widest ${passwordStrength === 4 ? 'text-green-600' : 'text-zinc-500'}`}>
                              {strengthLabels[passwordStrength - 1]}
                            </span>
                          </div>
                          <div className="flex gap-1 h-1">
                            {[1, 2, 3, 4].map((tier) => (
                              <div 
                                key={tier}
                                className={`flex-1 rounded-full transition-all duration-500 ${
                                  passwordStrength >= tier ? strengthColors[passwordStrength - 1] : 'bg-zinc-100'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-3">
                    <button
                      type="submit"
                      className="w-full bg-green-600 text-white py-4 rounded-xl font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-green-700 transition-all shadow-lg shadow-green-600/20"
                    >
                      {authStep === 'new_password' ? 'Update Password' : (identifierType === 'phone' || isForgotPassword ? 'Verify & Continue' : (isRegister ? 'Create Account' : 'Sign In'))}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    <button 
                      type="button"
                      onClick={() => {
                        setAuthStep('identifier');
                        setIsForgotPassword(false);
                        setFormData({ ...formData, password: '' });
                      }}
                      className="w-full text-xs text-zinc-500 font-bold hover:text-zinc-700 transition-colors"
                    >
                      Back to start
                    </button>
                  </div>
                </form>
              )}

              <div className="mt-8 pt-6 border-t border-zinc-100 text-center">
                <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mb-2">Business Owners</p>
                <button
                  onClick={() => {
                    onClose();
                    window.dispatchEvent(new CustomEvent('open-business-modal'));
                  }}
                  className="text-xs text-green-600 font-bold hover:underline"
                >
                  Promote your Avenue or Products
                </button>
              </div>

              {/* Developer Debug Panel */}
              {userDebugInfo && (
                <div className="mt-6 pt-6 border-t border-zinc-200">
                  <button
                    onClick={() => setShowDebugInfo(!showDebugInfo)}
                    className="w-full text-left flex items-center justify-between px-4 py-2 bg-zinc-50 rounded-lg hover:bg-zinc-100 transition-colors"
                  >
                    <span className="text-xs font-bold text-zinc-600">🔍 Debug Information</span>
                    <span className="text-xs text-zinc-400">{showDebugInfo ? '▲' : '▼'}</span>
                  </button>
                  
                  {showDebugInfo && (
                    <div className="mt-3 p-4 bg-zinc-50 rounded-lg space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="font-bold text-zinc-600">Account Exists:</span>
                        <span className={userDebugInfo.exists ? 'text-green-600' : 'text-red-600'}>
                          {userDebugInfo.exists ? '✓ Yes' : '✗ No'}
                        </span>
                      </div>
                      
                      {userDebugInfo.exists && (
                        <>
                          <div className="flex justify-between">
                            <span className="font-bold text-zinc-600">Email Confirmed:</span>
                            <span className={userDebugInfo.emailConfirmed ? 'text-green-600' : 'text-amber-600'}>
                              {userDebugInfo.emailConfirmed ? '✓ Yes' : '⚠ No'}
                            </span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="font-bold text-zinc-600">Password Set:</span>
                            <span className={userDebugInfo.hasPassword ? 'text-green-600' : 'text-red-600'}>
                              {userDebugInfo.hasPassword ? '✓ Yes' : '✗ No'}
                            </span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="font-bold text-zinc-600">User ID:</span>
                            <span className="text-zinc-500 font-mono text-[10px]">
                              {userDebugInfo.userId?.substring(0, 8)}...
                            </span>
                          </div>
                          
                          <div className="mt-4 pt-3 border-t border-zinc-200">
                            <p className="font-bold text-zinc-700 mb-2">Status:</p>
                            <p className="text-zinc-600">{userDebugInfo.message}</p>
                          </div>
                          
                          {!userDebugInfo.hasPassword && formData.password && (
                            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
                              <p className="font-bold text-amber-800 text-xs mb-1">⚠️ Password Not Set</p>
                              <p className="text-amber-700 text-[11px] mb-2">
                                This account was created without a password. Click below to set it now.
                              </p>
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/dev/set-password`, {
                                      method: 'POST',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${config.publicAnonKey}`
                                      },
                                      body: JSON.stringify({ 
                                        email: identifier,
                                        password: formData.password
                                      })
                                    });
                                    
                                    const data = await response.json();
                                    
                                    if (response.ok) {
                                      toast.success('Password Set!', {
                                        description: 'Your password has been set. Please sign in again.',
                                        duration: 5000
                                      });
                                      setUserDebugInfo({ ...userDebugInfo, hasPassword: true });
                                    } else {
                                      toast.error('Failed', { description: data.error });
                                    }
                                  } catch (err) {
                                    toast.error('Error', { description: 'Failed to set password' });
                                  }
                                }}
                                className="w-full bg-amber-600 text-white text-xs font-bold py-2 px-3 rounded hover:bg-amber-700 transition-colors"
                              >
                                Set Password Now
                              </button>
                            </div>
                          )}
                          
                          {!userDebugInfo.emailConfirmed && (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                              <p className="font-bold text-blue-800 text-xs mb-1">📧 Email Not Confirmed</p>
                              <p className="text-blue-700 text-[11px] mb-2">
                                Click below to verify your email now.
                              </p>
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await fetch(`https://${config.projectId}.supabase.co/functions/v1/make-server-684c7e75/dev/verify-email`, {
                                      method: 'POST',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${config.publicAnonKey}`
                                      },
                                      body: JSON.stringify({ email: identifier })
                                    });
                                    
                                    const data = await response.json();
                                    
                                    if (response.ok) {
                                      toast.success('Email Verified!', {
                                        description: 'Your email has been verified. Please sign in again.',
                                        duration: 5000
                                      });
                                      setUserDebugInfo({ ...userDebugInfo, emailConfirmed: true });
                                    } else {
                                      toast.error('Failed', { description: data.error });
                                    }
                                  } catch (err) {
                                    toast.error('Error', { description: 'Failed to verify email' });
                                  }
                                }}
                                className="w-full bg-blue-600 text-white text-xs font-bold py-2 px-3 rounded hover:bg-blue-700 transition-colors"
                              >
                                Verify Email Now
                              </button>
                            </div>
                          )}
                        </>
                      )}
                      
                      {!userDebugInfo.exists && (
                        <div className="mt-3 p-3 bg-zinc-100 border border-zinc-300 rounded">
                          <p className="text-zinc-600 text-[11px]">
                            No account found. Please sign up first.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}